import type { NostrEvent } from '@nostrify/nostrify';
import { sanitizeUrl } from './sanitizeUrl';

/**
 * Parsed representation of a Ditto profile theme (kind 16767) or theme
 * definition (kind 36767). See https://about.ditto.pub/reference
 */
export interface ProfileTheme {
  /** Page background color (hex, e.g. "#1a1a2e") */
  background: string;
  /** Primary text / foreground color (hex) */
  text: string;
  /** Accent color for buttons, links, focus rings (hex) */
  primary: string;
  /** Body font applied to all text globally */
  bodyFont?: ThemeFont;
  /** Title font applied to the display name */
  titleFont?: ThemeFont;
  /** Background media (image or video) */
  bg?: ThemeBackground;
  /** Human-readable theme name */
  title?: string;
  /** Reference to source theme definition (kind:pubkey:d-tag) */
  source?: string;
}

export interface ThemeFont {
  family: string;
  url?: string;
  role: 'body' | 'title';
}

export interface ThemeBackground {
  url: string;
  mode: 'cover' | 'tile';
  mimeType?: string;
  dim?: string;
  blurhash?: string;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function safeHex(value: string | undefined, fallback: string): string {
  if (value && HEX_RE.test(value.trim())) {
    return value.trim().toLowerCase();
  }
  return fallback;
}

/**
 * CSS `font-family` values are interpolated into inline styles. Strip anything
 * that could break out of the CSS context (quotes, semicolons, braces, etc.).
 */
function sanitizeFontFamily(family: string | undefined): string | undefined {
  if (!family) return undefined;
  const cleaned = family.replace(/["'`;{}()<>\\]/g, '').trim();
  return cleaned.length ? cleaned : undefined;
}

/**
 * Parse a kind 16767 / 36767 event into a ProfileTheme. Returns `undefined`
 * when the event is missing the required color tags.
 */
export function parseProfileTheme(event: NostrEvent | undefined | null): ProfileTheme | undefined {
  if (!event) return undefined;

  const colors: Record<string, string> = {};
  for (const tag of event.tags) {
    if (tag[0] === 'c' && tag[1] && tag[2]) {
      colors[tag[2]] = tag[1];
    }
  }

  if (!colors.background && !colors.text && !colors.primary) {
    return undefined;
  }

  const fonts: ThemeFont[] = [];
  for (const tag of event.tags) {
    if (tag[0] !== 'f' || !tag[1]) continue;
    const family = sanitizeFontFamily(tag[1]);
    if (!family) continue;
    // Format: ["f", family, url, role]. Legacy 3-element tags (no role) => body.
    const url = tag[2] ? sanitizeUrl(tag[2]) : undefined;
    const roleRaw = tag[3];
    const role: 'body' | 'title' = roleRaw === 'title' ? 'title' : 'body';
    fonts.push({ family, url, role });
  }

  let bg: ThemeBackground | undefined;
  const bgTag = event.tags.find((t) => t[0] === 'bg');
  if (bgTag) {
    const parts: Record<string, string> = {};
    for (let i = 1; i < bgTag.length; i++) {
      const entry = bgTag[i];
      const spaceIdx = entry.indexOf(' ');
      if (spaceIdx === -1) continue;
      const key = entry.slice(0, spaceIdx);
      const val = entry.slice(spaceIdx + 1);
      parts[key] = val;
    }
    const url = sanitizeUrl(parts.url);
    if (url) {
      bg = {
        url,
        mode: parts.mode === 'tile' ? 'tile' : 'cover',
        mimeType: parts.m,
        dim: parts.dim,
        blurhash: parts.blurhash,
      };
    }
  }

  const title = event.tags.find((t) => t[0] === 'title')?.[1];
  const source = event.tags.find((t) => t[0] === 'a')?.[1];

  return {
    background: safeHex(colors.background, '#ffffff'),
    text: safeHex(colors.text, '#0f172a'),
    primary: safeHex(colors.primary, '#6c3ce0'),
    bodyFont: fonts.find((f) => f.role === 'body'),
    titleFont: fonts.find((f) => f.role === 'title'),
    bg,
    title,
    source,
  };
}

/** A neutral default theme used when a profile has no active theme. */
export const DEFAULT_THEME: ProfileTheme = {
  background: '#0f172a',
  text: '#f8fafc',
  primary: '#6c3ce0',
};

/**
 * Convert a hex color to an "r, g, b" string for use in rgba() expressions.
 */
export function hexToRgb(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

/**
 * Perceived brightness of a hex color in the 0..1 range. Cheap and good enough
 * for "is this a dark surface?" decisions about overlays and borders.
 */
export function luminance(hex: string): number {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Determine whether a hex color is "dark" so we can choose contrasting overlays.
 */
export function isDark(hex: string): boolean {
  return luminance(hex) < 0.5;
}

/** WCAG relative luminance (gamma-decoded), used for real contrast math. */
function relativeLuminance(hex: string): number {
  const clean = hex.replace('#', '');
  const channel = (offset: number) => {
    const v = parseInt(clean.slice(offset, offset + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/**
 * WCAG contrast ratio between two hex colors (1..21).
 */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function hexToHsl(hex: string): Hsl {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex({ h, s, l }: Hsl): string {
  const sat = s / 100;
  const lig = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(lig, 1 - lig);
  const f = (n: number) =>
    Math.round((lig - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))) * 255);
  return '#' + [f(0), f(8), f(4)].map((v) => v.toString(16).padStart(2, '0')).join('');
}

/**
 * Minimum module/plate contrast. QR decoders binarize the image, so anything
 * at or above this reads reliably even in print.
 */
const QR_MIN_CONTRAST = 3;

/** A color only reads as "colorful" inside this saturation/lightness window. */
const COLORFUL_SAT_MIN = 15;
const COLORFUL_L_MIN = 20;
const COLORFUL_L_MAX = 80;

/**
 * Pick the module color from the theme, the way Ditto does: the accent
 * (`primary`) carries the brand and wins by default. The text color only takes
 * over when it is genuinely colorful *and* clearly more legible on the plate.
 */
function pickModuleColor(theme: ProfileTheme, plate: string): Hsl {
  const primary = hexToHsl(theme.primary);
  const text = hexToHsl(theme.text);

  const textIsColorful = text.s >= COLORFUL_SAT_MIN
    && text.l >= COLORFUL_L_MIN
    && text.l <= COLORFUL_L_MAX;
  if (!textIsColorful) return primary;

  return contrastRatio(theme.text, plate) > contrastRatio(theme.primary, plate) * 1.5
    ? text
    : primary;
}

/**
 * Walk a color's lightness toward the far end of the scale until it clears the
 * minimum contrast against the plate. Hue and saturation are preserved, so the
 * result still reads as the theme's color rather than plain black or white.
 */
function adjustToContrast(color: Hsl, plate: string): string {
  const lighten = isDark(plate);
  let { l } = color;
  let hex = hslToHex({ ...color, l });

  while (contrastRatio(hex, plate) < QR_MIN_CONTRAST && (lighten ? l < 100 : l > 0)) {
    l = lighten ? Math.min(100, l + 2) : Math.max(0, l - 2);
    hex = hslToHex({ ...color, l });
  }

  return hex;
}

/**
 * QR colors always come from the profile's Ditto theme — there is no color
 * picker. Light themes get a white plate, dark themes keep the card's own
 * background, and the modules are the themed color pushed just far enough in
 * lightness to stay scannable.
 */
export function qrColors(theme: ProfileTheme): { fg: string; bg: string } {
  const bg = isDark(theme.background) ? theme.background : '#ffffff';
  const module = pickModuleColor(theme, bg);
  return { fg: adjustToContrast(module, bg), bg };
}
